import { gql } from "@lytra/frontend-common/__generated__/gql";

export const CONVERSATION_WITH_MESSAGES_QUERY = gql(`
  query Ticket($conversationId: UUID!) {
    conversationOne(conversationId: $conversationId) {
      id
      createdAt
      isRead
      status
      ticketStatusId
      source
      title
      aiTitle
      summary
      priority
      languageCode
      contact {
        id
        emailAddress
        givenName
        familyName
      }
      company {
        id
        type
        name
        street
        streetNumber
        postalCode
        city
        country
      }
      assignedAgent {
        id
        givenName
        familyName
      }
      assignedTeam {
        id
        name
      }
      assignedById
      sla {
        policyName
        state
        responseDueAt
        firstResponseAt
        resolutionDueAt
        resolvedAt
        pausedSince
        pauseReason
        nextDueAt
      }
      connectorHealth {
        sourceType
        connectionStatus
        isConnected
        errorMessage
        lastSyncedAt
      }
      attachments {
        id
        name
        status
        size
        contentType
        summary
      }
      b2cCustomer {
        id
        givenName
        familyName
        emailAddress
        phoneNumber
      }
      messages {
        edges {
          node {
            id
            direction
            source
            createdAt
            receivedAt
            sentAt
            contentText
            contentHtml
            languageCode
            aiStatus
            answerConfidenceScore
            answerConfidenceHint
            answerConfidenceSignals
            emailSubject
            emailFullContentText
            emailFullContentHtml
            emailIsForwarded
            emailHasForwarderContent
            emailInternetMessageId
            recoveredFromMessageId
            isNote
            isInternal
            isForward
            forwardedMessageId
            forwardMailStatus
            forwardRecipients {
              kind
              userId
              label
              address
              isExternal
            }
            forwardRequestStates {
              userId
              status
            }
            emailFromAddress
            emailFromName
            parentMessageId
            editedAt
            deletedAt
            deliveryProblems {
              recipient
              action
              statusCode
              diagnostic
              reportedAt
            }
            emailRecipients {
              edges {
                node {
                  id
                  emailAddress
                  givenName
                  familyName
                }
              }
            }
            emailCcRecipients {
              edges {
                node {
                  id
                  emailAddress
                  givenName
                  familyName
                }
              }
            }
            emailForwardedSentAt
            emailForwardedSubject
            emailForwardedContentText
            emailForwardedFromContact {
              id
              emailAddress
              givenName
              familyName
            }
            emailForwardedRecipients {
              edges {
                node {
                  id
                  emailAddress
                  givenName
                  familyName
                }
              }
            }
            emailForwardedCcRecipients {
              edges {
                node {
                  id
                  emailAddress
                  givenName
                  familyName
                }
              }
            }
            agent {
              id
              emailAddress
              givenName
              familyName
            }
            contact {
              id
              emailAddress
              name
              givenName
              familyName
            }
            attachments {
              edges {
                node {
                  id
                  name
                  status
                  size
                  contentType
                  summary
                  isInline
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const GET_ATTACHMENT_URL_MUTATION = gql(`
  mutation GetAttachmentUrl($attachmentId: UUID!) {
    conversationGetAttachmentUrl(attachmentId: $attachmentId) {
      id
      filename
      status
      presignedUrl
    }
  }
`);

export const SET_READ_MUTATION = gql(`
  mutation SetConversationRead($conversationId: UUID!) {
    conversationSetRead(conversationId: $conversationId) {
      id
      isRead
    }
  }
`);

export const RECORD_VIEW_MUTATION = gql(`
  mutation RecordConversationView($messageId: UUID!) {
    conversationViewRecordView(messageId: $messageId, surface: "VENUS")
  }
`);

export const UPDATE_CONVERSATION_MUTATION = gql(`
  mutation UpdateConversation(
    $conversationId: UUID!
    $status: ConversationStatus
    $priority: ConversationPriority
    $agentId: UUID
    $teamId: UUID
    $ticketStatusId: UUID
  ) {
    conversationUpdate(
      conversationId: $conversationId
      status: $status
      priority: $priority
      agentId: $agentId
      teamId: $teamId
      ticketStatusId: $ticketStatusId
    ) {
      id
      status
      ticketStatusId
      priority
      assignedAgent {
        id
        givenName
        familyName
      }
      assignedTeam {
        id
        name
      }
      assignedById
    }
  }
`);

export const GET_AGENTS = gql(`
  query GetAgentsSimple {
    agents {
      id
      givenName
      familyName
      emailAddress
      avatarUrl
      avatarColor
    }
  }
`);

/** The tenant's ticket statuses, one flat list in board order (ADR-0078). */
export const GET_TICKET_STATUSES = gql(`
  query GetTicketStatuses {
    ticketStatuses {
      id
      name
      category
      position
      systemKey
      sla
      color
      deletable
      defaultSla
      defaultColor
    }
  }
`);

/** Whether the tenant has an SLA policy, so SLA filters and sorting apply (#888). */
export const SLA_ENABLED = gql(`
  query GetSlaEnabled {
    conversationSlaEnabled
  }
`);

export const GET_TEAMS = gql(`
  query GetTeams {
    teams {
      id
      name
      members {
        id
      }
    }
  }
`);

export const REPLY_MUTATION = gql(`
  mutation Reply($messageId: UUID!, $content: String!, $replyToAll: Boolean!, $replyToForwarder: Boolean!, $fromEmailAddress: String!, $attachmentIds: [UUID!]) {
    messageReply(messageId: $messageId, content: $content, replyToAll: $replyToAll, replyToForwarder: $replyToForwarder, fromEmailAddress: $fromEmailAddress, attachmentIds: $attachmentIds)
  }
`);

export const DISMISS_PARAGRAPH_MUTATION = gql(`
  mutation VenusDismissParagraph($messageId: UUID!, $targetText: String!) {
    messageDismissItem(messageId: $messageId, kind: PARAGRAPH, targetText: $targetText)
  }
`);

export const EDIT_PROPOSED_ANSWER_MUTATION = gql(`
  mutation EditProposedAnswer($messageId: UUID!, $newText: String!) {
    messageEditProposedAnswer(messageId: $messageId, newText: $newText)
  }
`);

export const REANNOTATE_MESSAGE_MUTATION = gql(`
  mutation ReannotateMessage($messageId: UUID!) {
    conversationReannotateMessage(messageId: $messageId) {
      id
      aiStatus
    }
  }
`);

export const TRANSLATE_CONVERSATION_QUERY = gql(`
  query TranslateConversation($conversationId: UUID!, $languageCode: String!) {
    conversationTranslate(conversationId: $conversationId, languageCode: $languageCode) {
      messageId
      contentText
      contentHtml
      emailSubject
      emailFullContentText
      emailFullContentHtml
      emailForwardedSubject
      emailForwardedContentText
    }
  }
`);
